import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v1Get, v1Post, v1Patch, v1Put, v1Delete } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, ChevronUp, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';

const errDetail = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

export function WorkCategoriesDialog({ dept, initKey, onClose, onOpenDept }) {
  const qc = useQueryClient();
  const catDraftInitRef = useRef(null);

  const [catForm, setCatForm] = useState({ label: '' });
  const [editCat, setEditCat] = useState(null);
  const [editCatLabel, setEditCatLabel] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [draftCategories, setDraftCategories] = useState([]);
  const [retiredCategoryIds, setRetiredCategoryIds] = useState([]);
  const [catDirty, setCatDirty] = useState(false);
  const [catDiscardConfirmOpen, setCatDiscardConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: workCategories = [], isLoading: catsLoading } = useQuery({
    queryKey: ['v1', 'work-categories-admin', dept?.id],
    queryFn: () => v1Get(`/master/departments/${dept.id}/work-categories`),
    enabled: !!dept?.id,
  });

  const { data: categoryTemplates = [] } = useQuery({
    queryKey: ['v1', 'work-category-templates'],
    queryFn: () => v1Get('/master/work-category-templates'),
    enabled: !!dept,
  });

  useEffect(() => {
    catDraftInitRef.current = null;
  }, [initKey]);

  useEffect(() => {
    if (!dept?.id || catsLoading) return;
    if (catDraftInitRef.current === dept.id) return;
    catDraftInitRef.current = dept.id;
    setDraftCategories(
      workCategories.map((c) => ({
        id: c.id,
        clientKey: c.id,
        label: c.label,
        originalLabel: c.label,
      })),
    );
    setRetiredCategoryIds([]);
    setCatDirty(false);
    setCatForm({ label: '' });
    setEditCat(null);
    setEditCatLabel('');
    setTemplateKey('');
  }, [dept?.id, catsLoading, workCategories]);

  const resetCatDialogState = () => {
    catDraftInitRef.current = null;
    onClose();
    setDraftCategories([]);
    setRetiredCategoryIds([]);
    setCatDirty(false);
    setCatForm({ label: '' });
    setEditCat(null);
    setEditCatLabel('');
    setTemplateKey('');
  };

  const requestCloseCatDialog = () => {
    if (catDirty) {
      setCatDiscardConfirmOpen(true);
      return;
    }
    resetCatDialogState();
  };

  const refreshCatOpenDept = async () => {
    if (!dept) return;
    const refreshed = await v1Get('/master/departments');
    qc.invalidateQueries({ queryKey: ['v1', 'departments'] });
    qc.invalidateQueries({ queryKey: ['v1-master-departments'] });
    qc.invalidateQueries({ queryKey: ['v1-work-categories'] });
    qc.invalidateQueries({ queryKey: ['v1', 'work-categories-admin', dept.id] });
    const next = (refreshed || []).find((d) => d.id === dept.id);
    if (next) onOpenDept(next);
  };

  const addWorkCategoryDraft = () => {
    if (!catForm.label.trim()) return toast.error('Category label is required');
    const label = catForm.label.trim();
    if (draftCategories.some((c) => c.label.toLowerCase() === label.toLowerCase())) {
      return toast.error('That category label already exists in this list.');
    }
    setDraftCategories((prev) => [
      ...prev,
      { id: null, clientKey: `new-${Date.now()}`, label, originalLabel: null },
    ]);
    setCatForm({ label: '' });
    setCatDirty(true);
  };

  const applyWorkCategoryEdit = () => {
    if (!editCat) return;
    if (!editCatLabel.trim()) return toast.error('Category label is required');
    const label = editCatLabel.trim();
    if (draftCategories.some((c) => c.clientKey !== editCat.clientKey && c.label.toLowerCase() === label.toLowerCase())) {
      return toast.error('That category label already exists in this list.');
    }
    setDraftCategories((prev) => prev.map((c) => (
      c.clientKey === editCat.clientKey ? { ...c, label } : c
    )));
    setEditCat(null);
    setEditCatLabel('');
    setCatDirty(true);
  };

  const retireWorkCategoryDraft = (cat) => {
    setDraftCategories((prev) => prev.filter((c) => c.clientKey !== cat.clientKey));
    if (cat.id) {
      setRetiredCategoryIds((prev) => [...prev, cat.id]);
    }
    if (editCat?.clientKey === cat.clientKey) {
      setEditCat(null);
      setEditCatLabel('');
    }
    setCatDirty(true);
  };

  const cloneWorkCategoryTemplateDraft = () => {
    if (!templateKey) return toast.error('Pick a template to clone');
    if (draftCategories.length > 0) {
      return toast.error('Clear the list first — clone is only available when no categories are in the draft.');
    }
    const template = categoryTemplates.find((t) => t.key === templateKey);
    if (!template?.categories?.length) return toast.error('Template not found.');
    setDraftCategories(
      template.categories.map((label, i) => ({
        id: null,
        clientKey: `tpl-${templateKey}-${i}`,
        label,
        originalLabel: null,
      })),
    );
    setTemplateKey('');
    setCatDirty(true);
    toast.message('Template added to draft — click Save changes to apply.');
  };

  const moveWorkCategoryDraft = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= draftCategories.length) return;
    setDraftCategories((prev) => {
      const next = [...prev];
      [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
      return next;
    });
    setCatDirty(true);
  };

  const saveAllCategoryChanges = async () => {
    if (!dept) return;
    if (!catDirty) return requestCloseCatDialog();
    setSubmitting(true);
    try {
      const labelUpdates = draftCategories.filter(
        (c) => c.id && c.label !== c.originalLabel,
      );
      for (const cat of labelUpdates) {
        await v1Patch(`/master/departments/${dept.id}/work-categories/${cat.id}`, {
          label: cat.label,
        });
      }

      const orderedIds = [];
      for (const cat of draftCategories) {
        if (cat.id) {
          orderedIds.push(cat.id);
        } else {
          const created = await v1Post(`/master/departments/${dept.id}/work-categories`, {
            label: cat.label,
          });
          orderedIds.push(created.id);
        }
      }

      for (const id of retiredCategoryIds) {
        await v1Delete(`/master/departments/${dept.id}/work-categories/${id}`);
      }

      if (orderedIds.length > 0) {
        await v1Put(`/master/departments/${dept.id}/work-categories/reorder`, { ids: orderedIds });
      }

      await refreshCatOpenDept();
      toast.success('Evaluation categories saved.');
      resetCatDialogState();
    } catch (e) {
      toast.error(errDetail(e, 'Could not save category changes.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) requestCloseCatDialog();
  };

  return (
    <>
      <Dialog open={!!dept} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-xl" data-testid="manage-categories-dialog">
          <DialogHeader>
            <DialogTitle>{dept ? `Evaluation categories · ${dept.name}` : 'Evaluation categories'}</DialogTitle>
            <DialogDescription>
              Work Assessment and Tech Panel question banks require at least one category per department.
              Changes are saved only when you click Save changes. Close discards unsaved edits.
            </DialogDescription>
          </DialogHeader>
          {dept && (
            <div className="space-y-4">
              {draftCategories.length === 0 && !catsLoading && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  No categories yet. Clone a template or add labels manually, then save.
                </div>
              )}
              {draftCategories.length === 0 && !catsLoading && (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[220px] flex-1">
                    <label className="text-xs font-medium text-muted-foreground">Clone template</label>
                    <Select value={templateKey} onValueChange={setTemplateKey}>
                      <SelectTrigger data-testid="clone-template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                      <SelectContent>
                        {categoryTemplates.map((t) => (
                          <SelectItem key={t.key} value={t.key}>{t.label} (6)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={cloneWorkCategoryTemplateDraft} disabled={submitting || !templateKey} data-testid="clone-template-confirm">
                    Add template
                  </Button>
                </div>
              )}
              <div className="rounded-xl border bg-secondary/30 p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Categories ({draftCategories.length}){catDirty ? ' · unsaved changes' : ''}
                </p>
                {catsLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : draftCategories.length === 0 ? (
                  <p className="text-xs text-muted-foreground">None in draft.</p>
                ) : (
                  <ul className="space-y-2">
                    {draftCategories.map((cat, index) => (
                      <li key={cat.clientKey} className="flex items-center gap-2 rounded-lg border bg-card px-2 py-1.5">
                        {editCat?.clientKey === cat.clientKey ? (
                          <>
                            <Input
                              value={editCatLabel}
                              onChange={(e) => setEditCatLabel(e.target.value)}
                              className="h-8 flex-1 text-sm"
                              data-testid={`edit-cat-input-${cat.clientKey}`}
                              onKeyDown={(e) => { if (e.key === 'Enter') applyWorkCategoryEdit(); }}
                            />
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={applyWorkCategoryEdit} aria-label="Apply edit">
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditCat(null)}>Cancel</Button>
                          </>
                        ) : (
                          <>
                            <span className="flex-1 text-sm font-medium">{cat.label}</span>
                            {!cat.id && (
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">New</span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={submitting || index === 0}
                              onClick={() => moveWorkCategoryDraft(index, -1)}
                              aria-label="Move up"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              disabled={submitting || index === draftCategories.length - 1}
                              onClick={() => moveWorkCategoryDraft(index, 1)}
                              aria-label="Move down"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => { setEditCat(cat); setEditCatLabel(cat.label); }}
                              aria-label={`Edit ${cat.label}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-rose-700 hover:bg-rose-50"
                              onClick={() => retireWorkCategoryDraft(cat)}
                              aria-label={`Remove ${cat.label}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                <Input
                  value={catForm.label}
                  onChange={(e) => setCatForm({ label: e.target.value })}
                  placeholder="New category label"
                  data-testid="new-cat-label-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') addWorkCategoryDraft(); }}
                />
                <Button onClick={addWorkCategoryDraft} disabled={submitting} data-testid="add-cat-confirm">
                  Add to draft
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={requestCloseCatDialog} disabled={submitting} data-testid="cat-dialog-close">
              Close
            </Button>
            <Button onClick={saveAllCategoryChanges} disabled={submitting || !catDirty} data-testid="cat-dialog-save">
              {submitting ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={catDiscardConfirmOpen} onOpenChange={setCatDiscardConfirmOpen}>
        <AlertDialogContent data-testid="cat-discard-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved category changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your edits to evaluation categories have not been saved. Close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cat-discard-cancel">Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setCatDiscardConfirmOpen(false);
                resetCatDialogState();
              }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="cat-discard-confirm"
            >
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
